import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Package,
  Network,
  Layers,
  AlertCircle,
  Loader,
  Search,
  Image
} from 'lucide-react';
import api from '../../utils/api';

interface Product {
  id_catalogo: string;
  title: string;
  Marca?: string;
  Cat?: string;
  sale_price?: number;
  sitio?: string;
  imagen?: string;
  catalog_brand?: string;
  catalog_category?: string;
}

interface EqualCluster {
  cluster_id: string;
  product_count: number;
  avg_confidence?: number;
  sites?: string;
  products?: Product[];
  referenceImage?: string;
}

interface SimilarCluster {
  cluster_id: string;
  cluster_name?: string;
  product_count: number;
  avg_confidence?: number;
  min_confidence?: number;
  max_confidence?: number;
  common_attributes?: any;
  distinctive_attributes?: any;
  equal_clusters: EqualCluster[];
}

interface Singleton {
  id_catalogo: string;
  title: string;
  Marca?: string;
  Cat?: string;
  sale_price?: number;
  sitio?: string;
}

interface ClustersViewProps {
  clientId: string;
  clientName: string;
}

const ClustersView: React.FC<ClustersViewProps> = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clusters, setClusters] = useState<SimilarCluster[]>([]);
  const [filteredClusters, setFilteredClusters] = useState<SimilarCluster[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [singletons, setSingletons] = useState<Singleton[]>([]);
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set());
  const [expandedEqualClusters, setExpandedEqualClusters] = useState<Set<string>>(new Set());
  const [summary, setSummary] = useState({
    total_similar_clusters: 0,
    total_equal_clusters: 0,
    total_singletons: 0
  });

  useEffect(() => {
    fetchClusters();
  }, [clientId]);

  useEffect(() => {
    // Filter clusters based on search term
    if (!searchTerm) {
      setFilteredClusters(clusters);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = clusters.filter(cluster => {
        const clusterName = cluster.cluster_name?.toLowerCase() || '';
        const clusterId = cluster.cluster_id.toLowerCase();
        const attributes = JSON.stringify(cluster.common_attributes).toLowerCase();
        
        return clusterName.includes(term) || 
               clusterId.includes(term) || 
               attributes.includes(term);
      });
      setFilteredClusters(filtered);
    }
  }, [searchTerm, clusters]);

  const fetchClusters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/api/clustering/all/${clientId}`, {
        headers: {
          'X-Client-Id': clientId,
          'X-Client-Name': clientName
        }
      });

      if (response.data.success) {
        setClusters(response.data.clusters || []);
        setFilteredClusters(response.data.clusters || []);
        setSingletons(response.data.singletons || []);
        setSummary(response.data.summary || {
          total_similar_clusters: 0,
          total_equal_clusters: 0,
          total_singletons: 0
        });
      }
    } catch (err: any) {
      console.error('Error fetching clusters:', err);
      setError(err.response?.data?.error || 'Error al cargar los clusters');
    } finally {
      setLoading(false);
    }
  };

  const toggleCluster = (clusterId: string) => {
    const newExpanded = new Set(expandedClusters);
    if (newExpanded.has(clusterId)) {
      newExpanded.delete(clusterId);
    } else {
      newExpanded.add(clusterId);
    }
    setExpandedClusters(newExpanded);
  };

  const toggleEqualCluster = async (clusterId: string, similarClusterId: string) => {
    const newExpanded = new Set(expandedEqualClusters);
    if (newExpanded.has(clusterId)) {
      newExpanded.delete(clusterId);
    } else {
      newExpanded.add(clusterId);
      // Load products for this equal cluster if not already loaded
      await loadEqualClusterProducts(clusterId, similarClusterId);
    }
    setExpandedEqualClusters(newExpanded);
  };

  const loadEqualClusterProducts = async (equalClusterId: string, similarClusterId: string) => {
    try {
      const response = await api.get(`/api/clustering/products/${equalClusterId}`, {
        headers: {
          'X-Client-Id': clientId,
          'X-Client-Name': clientName
        }
      });

      if (response.data.success) {
        const products = response.data.products || [];
        // Get reference image from first product
        const referenceImage = products[0]?.imagen || products[0]?.image || '';
        
        // Update the cluster with products
        setClusters(prevClusters => 
          prevClusters.map(cluster => {
            if (cluster.cluster_id === similarClusterId) {
              return {
                ...cluster,
                equal_clusters: cluster.equal_clusters.map(eqCluster => 
                  eqCluster.cluster_id === equalClusterId
                    ? { 
                        ...eqCluster, 
                        products: products,
                        referenceImage: referenceImage
                      }
                    : eqCluster
                )
              };
            }
            return cluster;
          })
        );
      }
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const formatPrice = (price: any) => {
    if (!price) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numPrice);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600">Cargando clusters...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar clusters por nombre, ID o atributos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Clusters Similares</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_similar_clusters}</p>
            </div>
            <Layers className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Clusters Iguales</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_equal_clusters}</p>
            </div>
            <Network className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Productos Individuales</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_singletons}</p>
            </div>
            <Package className="w-8 h-8 text-gray-500" />
          </div>
        </div>
      </div>

      {/* Clusters */}
      <div className="space-y-4">
        {searchTerm && (
          <p className="text-sm text-gray-600 mb-3">
            Mostrando {filteredClusters.length} de {clusters.length} clusters
          </p>
        )}
        {filteredClusters.map((cluster) => (
          <div key={cluster.cluster_id} className="bg-white rounded-lg shadow">
            {/* Similar Cluster Header */}
            <div 
              className="p-4 border-b cursor-pointer hover:bg-gray-50"
              onClick={() => toggleCluster(cluster.cluster_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {expandedClusters.has(cluster.cluster_id) ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                  <Layers className="w-5 h-5 text-blue-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {cluster.cluster_name || `Cluster Similar ${cluster.cluster_id.slice(0, 8)}`}
                    </h3>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className="text-sm text-gray-500">
                        {cluster.product_count} productos
                      </span>
                      <span className="text-sm text-gray-500">
                        {cluster.equal_clusters.length} sub-clusters
                      </span>
                      {cluster.avg_confidence && (
                        <span className="text-sm text-gray-500">
                          Confianza: {(cluster.avg_confidence * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Equal Clusters */}
            {expandedClusters.has(cluster.cluster_id) && (
              <div className="p-4 bg-gray-50">
                <div className="space-y-3">
                  {cluster.equal_clusters.map((equalCluster) => (
                    <div key={equalCluster.cluster_id} className="bg-white rounded-lg border">
                      {/* Equal Cluster Header */}
                      <div 
                        className="p-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleEqualCluster(equalCluster.cluster_id, cluster.cluster_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {expandedEqualClusters.has(equalCluster.cluster_id) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            
                            {/* Reference Image */}
                            {equalCluster.referenceImage ? (
                              <img 
                                src={equalCluster.referenceImage.startsWith('data:') 
                                  ? equalCluster.referenceImage 
                                  : `data:image/jpeg;base64,${equalCluster.referenceImage}`}
                                alt="Cluster reference"
                                className="w-10 h-10 object-cover rounded border"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
                                <Image className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            
                            <Network className="w-4 h-4 text-green-500" />
                            <div>
                              <span className="font-medium text-gray-800">
                                Sub-cluster {equalCluster.cluster_id.slice(0, 8)}
                              </span>
                              <div className="flex items-center space-x-3 mt-1">
                                <span className="text-xs text-gray-500">
                                  {equalCluster.product_count} productos
                                </span>
                                {equalCluster.sites && (
                                  <span className="text-xs text-gray-500">
                                    Sitios: {equalCluster.sites}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Products in Equal Cluster */}
                      {expandedEqualClusters.has(equalCluster.cluster_id) && equalCluster.products && (
                        <div className="border-t bg-gray-50">
                          <div className="p-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                              {equalCluster.products.map((product) => (
                                <div 
                                  key={product.id_catalogo} 
                                  className="bg-white rounded-lg border hover:shadow-md transition-shadow duration-200 overflow-hidden"
                                >
                                  {/* Mini Card Image */}
                                  <div className="aspect-square bg-gray-50 relative">
                                    {product.imagen ? (
                                      <img 
                                        src={product.imagen.startsWith('data:') || product.imagen.startsWith('http') 
                                          ? product.imagen 
                                          : `data:image/jpeg;base64,${product.imagen}`}
                                        alt={product.title || 'Product'}
                                        className="w-full h-full object-contain p-1"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const parent = target.parentElement;
                                          if (parent) {
                                            parent.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Package className="w-8 h-8 text-gray-300" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Mini Card Info */}
                                  <div className="p-2">
                                    <h5 className="text-xs font-medium text-gray-900 line-clamp-2 min-h-[2rem]">
                                      {product.title || `${product.Marca || product.catalog_brand || ''} ${product.Cat || product.catalog_category || ''}`.trim() || `ID: ${product.id_catalogo.slice(0, 8)}`}
                                    </h5>
                                    
                                    {(product.Marca || product.catalog_brand) && (
                                      <p className="text-xs text-gray-500 mt-1 truncate">
                                        {product.Marca || product.catalog_brand}
                                      </p>
                                    )}
                                    
                                    {product.sale_price && (
                                      <p className="text-sm font-bold text-gray-900 mt-1">
                                        {formatPrice(product.sale_price)}
                                      </p>
                                    )}
                                    
                                    {product.sitio && (
                                      <p className="text-xs text-gray-400 mt-1 truncate">
                                        {product.sitio}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {equalCluster.products.length === 0 && (
                              <div className="text-center py-8 text-gray-500">
                                <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                                <p className="text-sm">No hay productos en este cluster</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Singletons */}
        {singletons.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex items-center space-x-3">
                <Package className="w-5 h-5 text-gray-500" />
                <h3 className="font-semibold text-gray-900">
                  Productos Individuales ({singletons.length})
                </h3>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {singletons.slice(0, 12).map((product) => (
                  <div key={product.id_catalogo} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900 truncate">{product.title}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      {product.Marca && (
                        <span className="text-xs text-gray-500">{product.Marca}</span>
                      )}
                      {product.sitio && (
                        <span className="text-xs text-gray-500">• {product.sitio}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {singletons.length > 12 && (
                <p className="text-sm text-gray-500 mt-3 text-center">
                  Y {singletons.length - 12} productos más...
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClustersView;